// ============================================================
// THE BLADES AND THE MOTIONS (plan body-map H3; the bench's, as the user
// ruled them on 2026-10-09). A blade gives the hit its shape - Cleaving's
// great smear and bitten wedge, the Power Field by the damage it deals,
// Razor's hairline across the whole plate, Mono's unseen cut, the Ethereal
// ghost of a swing, the Lance's red-hot hole. A motion is what the blow does
// to the whole figure (its move is the plate's: SpecialMoments `moveOf`);
// here, what it leaves round it - Impetus's trail and dust, Graviton's
// ground ring, Concussive's heavy arcs.
// ============================================================

import type { ReactNode } from 'react';
import clsx from '../../../../utils/clsx';
import sx from '../../../../styles/PlateSpecials.module.css';
import { Bolts, Flash, jagged, Motes, radial, Rays, ringPoints, Smear, swingEnds, type Point } from './primitives';
import { forgeSparks, partGlow, swingOf, thrown, wholeLines, type LookCtx } from './kit';
import { timed } from '../timing';

/** Cleaving: a great blade's weight - a huge slower smear, a wedge bitten out, chunks thrown along the swing. */
function cleaving(c: LookCtx) {
  const { ang, len } = swingOf(c);
  const [nx, ny] = [-Math.sin(ang), Math.cos(ang)];
  const w = 90 * c.k;
  const tip = (side: number): Point => [
    c.at[0] - nx * w + side * Math.cos(ang) * w,
    c.at[1] - ny * w + side * Math.sin(ang) * w,
  ];
  return (
    <>
      <Smear
        id={`${c.id}cl`}
        at={c.at}
        ang={ang}
        len={Math.min(1100 * c.k, len * 1.4)}
        thick={260 * c.k}
        colour="var(--plate-sp-cleave)"
        start={c.start}
        dur={0.5}
        glow={c.glow}
      />
      <path
        className={clsx(sx.mark, sx.razorW)}
        d={`M${tip(-1).join(' ')} L${c.at.join(' ')} L${tip(1).join(' ')}`}
        fill="none"
        strokeWidth={18 * c.k}
        strokeLinejoin="round"
        style={timed(c.start + 0.12, 1)}
      />
      <Motes
        motes={thrown(c, { n: 8, dir: ang, spread: 0.7, dist: 600, size: 22, salt: 'ch' })}
        palette={sx.chunk}
        start={c.start}
        dur={0.8}
      />
    </>
  );
}

/** The Power Field on Energy: blue forge sparks and a short flash at the wound, long thin lightning along the swing. */
function pfEnergy(c: LookCtx) {
  const { ang, len } = swingOf(c);
  const sparks = forgeSparks(c, 46, 's');
  const streaks = Array.from({ length: 8 }, (_, i) => {
    const a = ang + (c.r(`la${i}`) - 0.5) * 0.9;
    const r = (600 + 500 * c.r(`lr${i}`)) * c.k * (i % 2 ? 1 : -1);
    return jagged(c.at, [c.at[0] + Math.cos(a) * r, c.at[1] + Math.sin(a) * r], 14, 60 * c.k, (s) => c.r(`lj${i}${s}`));
  });
  return (
    <>
      <Smear
        id={`${c.id}pf`}
        at={c.at}
        ang={ang}
        len={len}
        thick={150 * c.k}
        colour="var(--plate-sp-pf)"
        start={c.start}
        glow={c.glow}
      />
      <Flash at={c.at} r={220 * c.k} palette={sx.pfFlash} start={c.start} dur={0.28} glow={c.glow} />
      <Motes motes={sparks} palette={sx.pfSpark} start={c.start} dur={0.75} glow={c.glow} />
      <Bolts paths={streaks} palette={sx.pf} width={3 * c.k} start={c.start} dur={0.6} glow={c.glow} />
    </>
  );
}

/** The part's lines blown out of shape round the wound, under the disruption, out to `r`. */
const blown = (c: LookCtx, r: number, start: number, dur: number) =>
  partGlow(c, { r, palette: sx.white, start, dur, width: 44, filter: `url(#${c.u}-disrupt)` });

/** The Power Field on Impact (a tight burst) or Explosive (widening across the body, debris thrown out). */
function pfBurst(c: LookCtx, wide: boolean) {
  const { ang, len } = swingOf(c);
  const rings = wide ? [300, 620, 950] : [260];
  return (
    <>
      <Smear
        id={`${c.id}pf`}
        at={c.at}
        ang={ang}
        len={len}
        thick={150 * c.k}
        colour="var(--plate-sp-arc-2)"
        start={c.start}
        glow={c.glow}
      />
      {rings.map((r, i) => blown(c, r, c.start + 0.06 + i * 0.09, 0.45))}
      <Flash at={c.at} r={(wide ? 300 : 140) * c.k} palette={sx.pfFlash} start={c.start} dur={0.3} glow={c.glow} />
      {wide && (
        <Motes
          motes={thrown(c, { n: 26, dir: 0, spread: Math.PI * 2, dist: 620, size: 9, salt: 'pd' })}
          palette={sx.pfDebris}
          start={c.start}
          dur={0.8}
        />
      )}
    </>
  );
}

/** The Power Field on Rending: the pale smear, lightning on its edge, matter blown apart, sparks along the swing. */
function pfRending(c: LookCtx) {
  const { ang, len } = swingOf(c);
  const [A, B] = swingEnds(c.at, ang, len);
  const edge = [0, 1, 2].map((i) => jagged(A, B, 12, 60 * c.k, (s) => c.r(`pe${i}${s}`)));
  return (
    <>
      <Smear
        id={`${c.id}pf`}
        at={c.at}
        ang={ang}
        len={len}
        thick={150 * c.k}
        colour="var(--plate-sp-arc-2)"
        start={c.start}
        glow={c.glow}
      />
      <Bolts paths={edge} palette={sx.white} width={4 * c.k} start={c.start} dur={0.45} glow={c.glow} />
      {blown(c, 300, c.start + 0.08, 0.5)}
      <Motes
        motes={thrown(c, { n: 16, dir: ang, spread: 0.5, dist: 520, size: 7, salt: 'pr' })}
        palette={sx.pfDebris}
        start={c.start}
        dur={0.8}
      />
    </>
  );
}

/** Power Field: the disruption field on a swung edge - by the damage it deals (the user's three looks). */
function powerfield(c: LookCtx): ReactNode {
  if (c.type === 'E') return pfEnergy(c);
  if (c.type === 'I' || c.type === 'X') return pfBurst(c, c.type === 'X');
  return pfRending(c);
}

/** Razor's three hairlines: two split colours either side of a white one. */
const RAZOR = [
  { colour: sx.razorA, offset: -3, width: 2 },
  { colour: sx.razorB, offset: 3, width: 2 },
  { colour: sx.razorW, offset: 0, width: 2.5 },
];

/** Razor Sharp: it meets no resistance - a hairline through the part and across the whole plate, split into colour. */
function razor(c: LookCtx) {
  const { ang } = swingOf(c);
  // Right across the plate through the place, at the swing's angle (6000: wider than any frame).
  const [p, q] = swingEnds(c.at, ang, 6000);
  return RAZOR.map(({ colour, offset, width }) => (
    <line
      key={colour}
      className={clsx(sx.hairline, colour)}
      x1={p[0]}
      y1={p[1] + offset}
      x2={q[0]}
      y2={q[1] + offset}
      strokeWidth={width}
      style={timed(c.start)}
    />
  ));
}

/** Mono: too quick to see - a glint, a beat of nothing, then the line is simply there and the part parts a hair. */
function mono(c: LookCtx) {
  const { ang, len } = swingOf(c);
  const [A, B] = swingEnds(c.at, ang, len);
  const cut = { x1: A[0], y1: A[1], x2: B[0], y2: B[1] };
  return (
    <>
      <Flash at={c.at} r={60 * c.k} palette={sx.white} start={c.start} dur={0.25} glow={c.glow} />
      <line className={sx.gap} {...cut} style={timed(c.start + 0.32, 0, { '--gap': Math.round(30 * c.k) })} />
      <line
        className={clsx(sx.hairline, sx.razorW)}
        {...cut}
        strokeWidth={4 * c.k}
        filter={c.glow}
        style={timed(c.start + 0.32, 1)}
      />
    </>
  );
}

/** Ethereal: a blade only half in this world - its swing a ghost of a smear, the part rippling like mist after it. */
function ethereal(c: LookCtx) {
  const { ang, len } = swingOf(c);
  const [x0, y0, x1, y1] = c.partBox;
  return (
    <>
      <Smear
        id={`${c.id}et`}
        at={c.at}
        ang={ang}
        len={len}
        thick={130 * c.k}
        colour="var(--plate-sp-ether)"
        start={c.start}
        dur={0.6}
        glow={c.glow}
      />
      {partGlow(c, { r: Math.hypot(x1 - x0, y1 - y0), palette: sx.ether, dur: 0.7, filter: `url(#${c.u}-mist)` })}
    </>
  );
}

/** The Lance's hole: torn, never a clean circle - black at its heart, red-hot at the rim, cooling. */
function lanceHole(c: LookCtx, dos: number) {
  const hr = (46 + 12 * dos) * c.k;
  const pts = ringPoints(c.at, 13, (i) => hr * (0.75 + 0.45 * c.r(`h${i}`)));
  return (
    <>
      <radialGradient id={`${c.id}hole`}>
        <stop offset={0} className={sx.holeCore} />
        <stop offset={0.62} className={sx.holeDark} />
        <stop offset={0.8} className={sx.holeHot} />
        <stop offset={0.92} className={sx.holeGlow} />
        <stop offset={1} className={sx.holeFade} stopOpacity={0} />
      </radialGradient>
      <polygon
        className={sx.mark}
        points={pts}
        fill={`url(#${c.id}hole)`}
        filter={c.glow}
        style={timed(c.start + 0.08, 2.4)}
      />
      <polygon className={sx.rimCool} points={pts} strokeWidth={6 * c.k} style={timed(c.start + 0.08)} />
    </>
  );
}

/** Lance (user): Felling's impact, stronger and cleaner - a great flash, long rays - leaving a red-hot hole. */
function lance(c: LookCtx) {
  const dos = 1 + Math.floor(c.r('dos') * 4);
  const kk = (1 + 0.25 * dos) * c.k;
  return (
    <>
      <Flash at={c.at} r={420 * kk} palette={sx.white} start={c.start} dur={0.65} glow={c.glow} />
      <Rays
        at={c.at}
        rays={radial(8, 520 * kk, 0, c.r, 0.2)}
        palette={sx.white}
        width={4 * c.k}
        start={c.start}
        dur={0.35}
        glow={c.glow}
      />
      {partGlow(c, { r: 380 * (1 + 0.25 * dos), palette: sx.white, dur: 0.6 })}
      {lanceHole(c, dos)}
    </>
  );
}

/** Which way Impetus throws the figure: the seed decides, the same on every screen (its move reads the same). */
export const impetusSide = (r: (salt: string) => number) => (r('side') < 0.5 ? -1 : 1);

/** Impetus: thrown back hard - a trail of where it stood left behind, dust at its feet. */
function impetus(c: LookCtx) {
  const side = impetusSide(c.r);
  const feet: Point = [(c.figureBox[0] + c.figureBox[2]) / 2 + side * 120 * c.k, c.figureBox[3]];
  const dir = side > 0 ? 0 : Math.PI;
  const dust = thrown(c, { n: 10, dir, spread: 0.5, dist: 300, size: 10, salt: 'd', from: feet, lift: -60 });
  return (
    <>
      {[1, 2, 3].map((k) => (
        <g key={k}>
          {wholeLines(c, sx.ghost, timed(c.start + 0.05 * k, 0, { '--from': 0.4 - 0.1 * k }), {
            transform: `translate(${side * 170 * k * c.k} 0)`,
          })}
        </g>
      ))}
      <Motes motes={dust} palette={sx.dust} start={c.start} dur={0.8} />
    </>
  );
}

/** Graviton: crushed toward the floor and held a moment (its move), the floor shuddering under it - a ring out. */
function graviton(c: LookCtx) {
  const [x0, , x1, y1] = c.figureBox;
  const w = (x1 - x0) / 2;
  return (
    <ellipse
      className={sx.groundRing}
      cx={(x0 + x1) / 2}
      cy={y1 + 20 * c.k}
      rx={w}
      ry={w * 0.17}
      strokeWidth={6 * c.k}
      style={timed(c.start + 0.1)}
    />
  );
}

/** Concussive: heavy arcs over the whole body - the blow's force through all of it (and its kick: the move). */
function concussive(c: LookCtx) {
  const [x0, y0, x1, y1] = c.figureBox;
  const paths = Array.from({ length: 9 }, (_, i) => {
    const to: Point = [x0 + (x1 - x0) * c.r(`cx${i}`), y0 + (y1 - y0) * (0.05 + 0.9 * c.r(`cy${i}`))];
    return jagged(c.at, to, 10, 140 * c.k, (s) => c.r(`cj${i}${s}`));
  });
  return <Bolts paths={paths} palette={sx.arc} width={11 * c.k} start={c.start} dur={0.7} glow={c.glow} />;
}

/** The blades' and the motions' looks, by key. */
export const BLADE_LOOKS: Record<string, (c: LookCtx) => ReactNode> = {
  cleaving,
  powerfield,
  razor,
  mono,
  ethereal,
  lance,
  impetus,
  graviton,
  concussive,
};
